import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'reactstrap';
import { IoClose } from 'react-icons/io5';
import { IoArrowBack } from 'react-icons/io5';
import { connect } from 'react-redux';
import { 
  toggleChooseImageModal,
  toggleImageLayoutModal,
  getOrganizationImageDesigns,
  getDesignById,
  handleEditMetaDataFromResults,
  toggleSpecificImageLayoutModal
} from '../../../../redux/actions/linkPreview';

import './ChooseImageModal.css';

const ChooseImageModal = (props) => {
  const { 
    chooseImageModal,
    toggleChooseImageModal,
    toggleImageLayoutModal,
    getOrganizationImageDesigns,
    getDesignById,
    handleEditMetaDataFromResults,
    toggleSpecificImageLayoutModal,
    imageDesigns,
    selectedDesign,
    imageLayouts
  } = props;

  const { open: visible } = chooseImageModal;
  const { designs, loading } = imageDesigns;
  const designLoading = selectedDesign.loading;

  const [step, setStep] = useState(1);
  const [selectedOption, setSelectedOption] = useState(null);
  const [currentDesign, setCurrentDesign] = useState(null);
  const [copiedItemId, setCopiedItemId] = useState(null);
  const [copied, setCopied] = useState(false);

  // Reset step when modal is opened/closed
  useEffect(() => {
    if (visible) {
      setStep(1);
      setSelectedOption(null);
      setCurrentDesign(null);
      // If designs are empty, fetch them
      if (designs.length === 0 && !loading) {
        getOrganizationImageDesigns();
      }
    }
  }, [visible]);

  // Helper function to get layout image URL from layout ID
  const getLayoutImageUrl = (layoutId) => {
    if (!imageLayouts.layouts) return '';
    const layout = imageLayouts.layouts.find(layout => layout.id === layoutId);
    return layout ? layout.imageUrl : '/img/placeholder-design.png';
  };

  // Helper function to get the best available image for a design
  const getDesignImageUrl = (design) => {
    // First try to use an existing rendered image
    if (design.DesignParameters && design.DesignParameters.length > 0) {
      return design.DesignParameters[0].cdnUrl;
    }
    
    // If no rendered images, use the layout image
    return getLayoutImageUrl(design.layoutId);
  };

  const handleClose = () => {
    toggleChooseImageModal(false);
  };

  const handleCreateNewDesign = () => {
    // Close this modal and open the design modal
    toggleChooseImageModal(false);
    toggleImageLayoutModal(true);
  };

  const handleChooseExistingImage = () => {
    setSelectedOption('existingImage');
    setStep(2);
  };

  const handleRenderNewImage = () => {
    setSelectedOption('renderNew');
    setStep(2);
  };

  const handleBackToOptions = () => {
    setStep(1);
    setSelectedOption(null);
    setCurrentDesign(null);
  };

  const handleBackToDesigns = () => {
    setStep(2);
    setCurrentDesign(null);
  };

  const handleSelectDesign = (design) => {
    if (selectedOption === 'existingImage') {
      setCurrentDesign(design);
      // Fetch detailed design data with images
      getDesignById(design.id, 1, 10);
      setStep(3);
    } else if (selectedOption === 'renderNew') {
      // Find the layout associated with this design
      const layout = imageLayouts.layouts.find(layout => layout.id === design.layoutId);
      
      if (layout) {
        // Close this modal
        toggleChooseImageModal(false);
        
        // Open the ImageTemplateModal in render mode with the selected design's layout
        toggleSpecificImageLayoutModal(true, layout);
        
        // Set the current design ID for the createImage function to use
        setCurrentDesign(design);
      } else {
        console.error('Layout not found for design:', design);
      }
    }
  };

  const handleSelectImage = (imageUrl) => {
    // Update the image URL in the link preview form
    handleEditMetaDataFromResults('image', imageUrl);
    // Close the modal
    toggleChooseImageModal(false);
  };

  const copyUrlToClipboard = (url, id) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setCopiedItemId(id);
      setTimeout(() => {
        setCopied(false);
        setCopiedItemId(null);
      }, 1000); // Reset after 1 second
    }, (err) => {
      console.error('Failed to copy', err);
    });
  };

  // Render back button only when step > 1
  const renderBackButton = () => {
    if (step === 1) return null;
    
    return (
      <button 
        className="back-button" 
        onClick={step === 2 ? handleBackToOptions : handleBackToDesigns}
      >
        <IoArrowBack size={20} />
        <span>{step === 2 ? 'Back' : 'Back to Designs'}</span>
      </button>
    );
  };

  // Step 1: Show the three options
  const renderStep1 = () => (
    <div className="choose-image-modal__content">
      <h3>Choose an Option</h3>
      
      <div className="choose-image-modal__options">
        <div className="option-card" onClick={handleChooseExistingImage}>
          <div className="option-card__icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM13.96 12.29L11.21 15.83L9.25 13.47L6.5 17H17.5L13.96 12.29Z" fill="#2CBD6B"/>
            </svg>
          </div>
          <h4>Choose Existing Image</h4>
          <p>Select from your previously generated images</p>
        </div>
        
        <div className="option-card" onClick={handleRenderNewImage}>
          <div className="option-card__icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM5 19V5H19V19H5ZM12 6C9.79 6 8 7.79 8 10C8 12.21 9.79 14 12 14C14.21 14 16 12.21 16 10C16 7.79 14.21 6 12 6ZM12 12C10.9 12 10 11.1 10 10C10 8.9 10.9 8 12 8C13.1 8 14 8.9 14 10C14 11.1 13.1 12 12 12ZM18 16.43C18 14.62 14.67 13.5 12 13.5C9.33 13.5 6 14.62 6 16.43V18H18V16.43Z" fill="#2CBD6B"/>
            </svg>
          </div>
          <h4>Render New Image</h4>
          <p>Generate a new image from an existing design</p>
        </div>
        
        <div className="option-card" onClick={handleCreateNewDesign}>
          <div className="option-card__icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM12 6C9.24 6 7 8.24 7 11C7 13.76 9.24 16 12 16C14.76 16 17 13.76 17 11C17 8.24 14.76 6 12 6ZM12 14C10.34 14 9 12.66 9 11C9 9.34 10.34 8 12 8C13.66 8 15 9.34 15 11C15 12.66 13.66 14 12 14Z" fill="#2CBD6B"/>
              <path d="M7 19H17V17H7V19Z" fill="#2CBD6B"/>
              <path d="M15 13H17V7H15V13Z" fill="#2CBD6B"/>
              <path d="M7 13H9V7H7V13Z" fill="#2CBD6B"/>
            </svg>
          </div>
          <h4>Create New Design</h4>
          <p>Design a new template for your images</p>
        </div>
      </div>
    </div>
  );

  // Step 2: Show designs
  const renderStep2 = () => (
    <div className="choose-image-modal__content">
      <div className="choose-image-modal__header">
        <h3>
          {selectedOption === 'existingImage' ? 'Choose a Design' : 'Select a Design to Render'}
        </h3>
      </div>
      
      {loading ? (
        <div className="designs-loading">
          <div className="spinner-border text-success" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : designs.length === 0 ? (
        <div className="no-designs">
          <p>No designs found. Please create a design first.</p>
          <Button color="primary" onClick={handleCreateNewDesign}>Create New Design</Button>
        </div>
      ) : (
        <div className="designs-grid">
          {designs.map((design) => (
            <div 
              key={design.id} 
              className="design-card" 
              onClick={() => handleSelectDesign(design)}
            >
              <div className="design-card__image">
                <img 
                  src={getDesignImageUrl(design)} 
                  alt={design.name || 'Design'} 
                  className="design-image"
                />
              </div>
              <div className="design-card__info">
                <h4>{design.name || 'Unnamed Design'}</h4>
                <p>Created: {new Date(design.createdAt).toLocaleDateString()}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Step 3: Show images for a selected design
  const renderStep3 = () => {
    const designWithImages = selectedDesign.design;
    const hasImages = designWithImages && designWithImages.DesignParameters && designWithImages.DesignParameters.length > 0;

    return (
      <div className="choose-image-modal__content">
        <div className="choose-image-modal__header">
          <h3>Choose an Image</h3>
        </div>
        
        {designLoading ? (
          <div className="designs-loading">
            <div className="spinner-border text-success" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        ) : !hasImages ? (
          <div className="no-designs">
            <p>No images found for this design.</p>
            <Button color="primary" onClick={handleBackToDesigns}>Choose Another Design</Button>
          </div>
        ) : (
          <div className="images-container">
            {designWithImages.DesignParameters.map((image, i) => (
              <div key={`image-${i}`} className="image-card">
                <div className="image-card__preview" onClick={() => handleSelectImage(image.cdnUrl)}>
                  <img src={image.cdnUrl} alt={`Generated image ${i+1}`} />
                </div>
                <div className="image-card__toolbar">
                  <button 
                    className="url-copy-button" 
                    onClick={(e) => {
                      e.stopPropagation();
                      copyUrlToClipboard(image.cdnUrl, `image-${i}`);
                    }}
                  >
                    {copied && copiedItemId === `image-${i}` ? 'Copied!' : 'Copy URL'}
                  </button>
                  <button 
                    className="select-button" 
                    onClick={() => handleSelectImage(image.cdnUrl)}
                  >
                    Select
                  </button>
                </div>
                <div className="image-card__info">
                  <p>Created: {new Date(image.createdAt).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal isOpen={visible} toggle={handleClose} className={`choose-image-modal ${step === 3 ? 'large' : ''}`}>
      <div className="modal__close-btn" onClick={handleClose}>
        <IoClose size={26} />
      </div>
      
      {renderBackButton()}
      
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  chooseImageModal: state.linkPreview.chooseImageModal,
  imageDesigns: state.linkPreview.imageDesigns,
  selectedDesign: state.linkPreview.selectedDesign,
  imageLayouts: state.linkPreview.imageLayouts
});

const mapDispatchToProps = (dispatch) => ({
  toggleChooseImageModal: (open) => dispatch(toggleChooseImageModal(open)),
  toggleImageLayoutModal: (open) => dispatch(toggleImageLayoutModal(open)),
  getOrganizationImageDesigns: () => dispatch(getOrganizationImageDesigns()),
  getDesignById: (designId, page, limit) => dispatch(getDesignById(designId, page, limit)),
  handleEditMetaDataFromResults: (name, value) => dispatch(handleEditMetaDataFromResults(name, value)),
  toggleSpecificImageLayoutModal: (open, layout) => dispatch(toggleSpecificImageLayoutModal(open, layout))
});

export default connect(mapStateToProps, mapDispatchToProps)(ChooseImageModal); 